import Component, { Topic } from 'component';

const STORAGE_PREFIX = 'mlm:wishlist:customer:';
const LIMIT = 100;
const TOPIC_UPDATE = 'mlm-wishlist:update';
const HANDLE_RE = /^[a-z0-9-]+$/;
const FETCH_CONCURRENCY = 6;

function storageKey(customerId) {
	return STORAGE_PREFIX + customerId;
}

function readList(customerId) {
	if (!customerId) return [];
	try {
		const raw = window.localStorage.getItem(storageKey(customerId));
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((h) => typeof h === 'string' && HANDLE_RE.test(h));
	} catch (e) {
		return [];
	}
}

function writeList(customerId, list) {
	if (!customerId) return;
	try {
		window.localStorage.setItem(storageKey(customerId), JSON.stringify(list));
	} catch (e) {
		/* quota exceeded — ignore */
	}
}

function broadcast(customerId, list) {
	const data = { customerId: customerId, list: list };
	try {
		window.document.dispatchEvent(new CustomEvent(TOPIC_UPDATE, { detail: data }));
	} catch (e) {
		/* no-op */
	}
}

function sanitizeHandle(handle) {
	if (typeof handle !== 'string') return '';
	if (!HANDLE_RE.test(handle)) return '';
	return handle;
}

async function fetchInBatches(handles, fn, concurrency) {
	const results = new Array(handles.length);
	let next = 0;
	const workers = new Array(Math.min(concurrency, handles.length))
		.fill(null)
		.map(async () => {
			while (next < handles.length) {
				const idx = next++;
				results[idx] = await fn(handles[idx]);
			}
		});
	await Promise.all(workers);
	return results;
}

/**
 * <x-mlm-wishlist-toggle>
 * Heart button on product cards & product page.
 */
customElements.define(
	'x-mlm-wishlist-toggle',
	class extends Component {
		render() {
			this.button = this.querySelector('[data-mlm-wishlist-toggle-button]');
			this.handle = sanitizeHandle(this.getAttribute('data-product-handle') || '');
			this.customerId = this.getAttribute('data-customer-id') || '';
			this.loginUrl = this.getAttribute('data-login-url') || '/account/login';
			this.labelAdd = this.getAttribute('data-label-add') || 'Add to favorites';
			this.labelRemove = this.getAttribute('data-label-remove') || 'Remove from favorites';
			this.labelLoginRequired =
				this.getAttribute('data-label-login-required') || 'Please log in to use favorites';
			this.labelFull = this.getAttribute('data-label-full') || 'Wishlist is full';
			if (!this.button || !this.handle) return;
			this.syncState();
			this.on(this.button, 'click', this.onClick.bind(this));
			this.docListener = (e) => {
				if (e.detail && e.detail.customerId === this.customerId) {
					this.syncState();
				}
			};
			window.document.addEventListener(TOPIC_UPDATE, this.docListener);
		}
		disconnectedCallback() {
			if (this.docListener) {
				window.document.removeEventListener(TOPIC_UPDATE, this.docListener);
			}
			if (super.disconnectedCallback) super.disconnectedCallback();
		}
		isActive() {
			if (!this.customerId) return false;
			return readList(this.customerId).indexOf(this.handle) !== -1;
		}
		syncState() {
			const active = this.isActive();
			this.classList.toggle('mlm-wishlist-toggle--active', active);
			this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
			this.button.setAttribute('aria-label', active ? this.labelRemove : this.labelAdd);
		}
		onClick(e) {
			e.preventDefault();
			e.stopPropagation();
			if (!this.customerId) {
				this.pub(Topic.SHOW_TOAST, { type: 'info', msg: this.labelLoginRequired });
				const target =
					this.loginUrl +
					(this.loginUrl.indexOf('?') === -1 ? '?' : '&') +
					'checkout_url=' +
					encodeURIComponent(window.location.pathname + window.location.search);
				window.setTimeout(() => {
					window.location.href = target;
				}, 600);
				return;
			}
			const list = readList(this.customerId);
			const idx = list.indexOf(this.handle);
			if (idx !== -1) {
				list.splice(idx, 1);
			} else {
				if (list.length >= LIMIT) {
					this.pub(Topic.SHOW_TOAST, { type: 'error', msg: this.labelFull });
					return;
				}
				list.push(this.handle);
			}
			writeList(this.customerId, list);
			broadcast(this.customerId, list);
			this.syncState();
		}
	}
);

/**
 * <x-mlm-wishlist-indicator>
 * Header badge counter — listens for storage and in-tab update events.
 */
customElements.define(
	'x-mlm-wishlist-indicator',
	class extends Component {
		render() {
			this.customerId = this.getAttribute('data-customer-id') || '';
			this.counterEl = this.querySelector('[data-mlm-wishlist-indicator-count]');
			this.update();
			this.docListener = (e) => {
				if (e.detail && e.detail.customerId === this.customerId) {
					this.update();
				}
			};
			window.document.addEventListener(TOPIC_UPDATE, this.docListener);
			this.storageListener = (e) => {
				if (this.customerId && e.key === storageKey(this.customerId)) {
					this.update();
				}
			};
			window.addEventListener('storage', this.storageListener);
		}
		disconnectedCallback() {
			if (this.docListener) {
				window.document.removeEventListener(TOPIC_UPDATE, this.docListener);
			}
			if (this.storageListener) {
				window.removeEventListener('storage', this.storageListener);
			}
			if (super.disconnectedCallback) super.disconnectedCallback();
		}
		update() {
			const count = this.customerId ? readList(this.customerId).length : 0;
			this.toggleAttribute('data-empty', count === 0);
			if (this.counterEl) {
				this.counterEl.textContent = String(count);
			}
		}
	}
);

/**
 * <x-mlm-wishlist-page>
 * Reads handles from URL `?items=` (shared) or localStorage (own), fetches each
 * product card via the `wishlist-card` view template, and injects them.
 */
customElements.define(
	'x-mlm-wishlist-page',
	class extends Component {
		render() {
			this.customerId = this.getAttribute('data-customer-id') || '';
			this.grid = this.querySelector('[data-mlm-wishlist-grid]');
			this.emptyEl = this.querySelector('[data-mlm-wishlist-empty]');
			this.emptyGuestEl = this.querySelector('[data-mlm-wishlist-empty-guest]');
			this.sharedBanner = this.querySelector('[data-mlm-wishlist-shared-banner]');
			this.actionsEl = this.querySelector('[data-mlm-wishlist-actions]');

			const params = new URLSearchParams(window.location.search);
			const rawUrlItems = (params.get('items') || '')
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			const urlItems = rawUrlItems.map(sanitizeHandle).filter(Boolean);

			let handles;
			if (urlItems.length > 0) {
				handles = urlItems;
				this.isSharedView = !this.customerId;
			} else if (this.customerId) {
				const ownList = readList(this.customerId);
				if (ownList.length > 0) {
					params.set('items', ownList.join(','));
					window.location.replace(window.location.pathname + '?' + params.toString());
					return;
				}
				handles = [];
				this.isSharedView = false;
			} else {
				handles = [];
				this.isSharedView = false;
			}

			if (this.isSharedView && this.sharedBanner) {
				this.sharedBanner.style.display = '';
			}

			if (handles.length === 0) {
				this.showEmpty();
				return;
			}

			this.loadCards(handles);

			if (!this.isSharedView) {
				this.docListener = (e) => {
					if (e.detail && e.detail.customerId === this.customerId) {
						this.refresh();
					}
				};
				window.document.addEventListener(TOPIC_UPDATE, this.docListener);
			}
		}
		disconnectedCallback() {
			if (this.docListener) {
				window.document.removeEventListener(TOPIC_UPDATE, this.docListener);
			}
			if (super.disconnectedCallback) super.disconnectedCallback();
		}
		showEmpty() {
			if (this.grid) this.grid.style.display = 'none';
			if (this.actionsEl) this.actionsEl.style.display = 'none';
			if (this.customerId) {
				if (this.emptyEl) this.emptyEl.style.display = '';
			} else if (this.emptyGuestEl) {
				this.emptyGuestEl.style.display = '';
			} else if (this.emptyEl) {
				this.emptyEl.style.display = '';
			}
		}
		hideEmpty() {
			if (this.emptyEl) this.emptyEl.style.display = 'none';
			if (this.emptyGuestEl) this.emptyGuestEl.style.display = 'none';
			if (this.grid) this.grid.style.display = '';
			if (this.actionsEl) this.actionsEl.style.display = '';
		}
		async loadCards(handles) {
			this.hideEmpty();
			const results = await fetchInBatches(
				handles,
				async (handle) => {
					try {
						const url = '/products/' + encodeURIComponent(handle) + '?view=wishlist-card';
						const res = await window.fetch(url, {
							headers: { Accept: 'text/html' },
							credentials: 'same-origin'
						});
						if (!res.ok) return { handle: handle, html: null };
						const html = await res.text();
						return { handle: handle, html: html };
					} catch (e) {
						return { handle: handle, html: null };
					}
				},
				FETCH_CONCURRENCY
			);
			if (!this.grid) return;
			this.grid.innerHTML = '';
			let visible = 0;
			results.forEach(({ html }) => {
				if (!html) return;
				const wrap = document.createElement('div');
				wrap.innerHTML = html.trim();
				const node = wrap.firstElementChild;
				if (node && node.matches && node.matches('[data-mlm-wishlist-item]')) {
					this.grid.appendChild(node);
					visible++;
				}
			});
			if (visible === 0) {
				this.showEmpty();
				return;
			}
			this.applySharedView();
			this.bindRemoveButtons();
		}
		applySharedView() {
			if (!this.isSharedView) return;
			const buttons = this.querySelectorAll('[data-mlm-wishlist-remove]');
			buttons.forEach((b) => b.remove());
		}
		bindRemoveButtons() {
			if (this.isSharedView) return;
			const buttons = this.querySelectorAll('[data-mlm-wishlist-remove]');
			buttons.forEach((btn) => {
				this.on(btn, 'click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					const handle = sanitizeHandle(btn.getAttribute('data-mlm-wishlist-remove') || '');
					if (!handle) return;
					if (this.customerId) {
						const list = readList(this.customerId);
						const idx = list.indexOf(handle);
						if (idx !== -1) {
							list.splice(idx, 1);
							writeList(this.customerId, list);
							broadcast(this.customerId, list);
						}
					}
					const card = this.querySelector('[data-mlm-wishlist-item="' + handle + '"]');
					if (card) card.remove();
					this.updateUrlAfterRemoval();
					if (this.grid && this.grid.children.length === 0) {
						this.showEmpty();
					}
				});
			});
		}
		updateUrlAfterRemoval() {
			if (!this.customerId) return;
			const list = readList(this.customerId);
			const params = new URLSearchParams(window.location.search);
			if (list.length === 0) {
				params.delete('items');
			} else {
				params.set('items', list.join(','));
			}
			const qs = params.toString();
			const newUrl = window.location.pathname + (qs ? '?' + qs : '');
			window.history.replaceState(null, '', newUrl);
		}
		refresh() {
			if (this.isSharedView) return;
			const handles = this.customerId ? readList(this.customerId) : [];
			if (handles.length === 0) {
				if (this.grid) this.grid.innerHTML = '';
				this.showEmpty();
				return;
			}
			this.loadCards(handles);
		}
	}
);

/**
 * <x-mlm-wishlist-share>
 * Web Share API on supported devices, clipboard fallback otherwise.
 */
customElements.define(
	'x-mlm-wishlist-share',
	class extends Component {
		render() {
			const fallbackMsg = this.getAttribute('data-fallback-msg') || 'Link copied';
			const shareTitle = this.getAttribute('data-share-title') || document.title;
			this.on(this, 'click', (e) => {
				e.preventDefault();
				const url = window.location.href;
				if ('share' in window.navigator) {
					window.navigator.share({ url: url, title: shareTitle }).catch(() => {});
				} else if (window.navigator.clipboard) {
					window.navigator.clipboard.writeText(url).then(() => {
						this.pub(Topic.SHOW_TOAST, { type: 'success', msg: fallbackMsg });
					});
				}
			});
		}
	}
);
